import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Chip,
  Select,
  MenuItem as SelectMenuItem,
  FormControl,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Folder as ProjectIcon,
  VideoLibrary as ReelIcon,
  Publish as PublishIcon,
  Analytics as AnalyticsIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  Notifications as NotificationsIcon,
  ChevronLeft as ChevronLeftIcon,
  Category as TemplateIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useProjects } from '../../contexts/ProjectContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import Footer from '../Footer/Footer';
import Header from '../Header/Header';

const drawerWidth = 280;

const getMenuItems = (t: any) => [
  { title: t('navigation.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
  { title: t('navigation.projects'), icon: <ProjectIcon />, path: '/projects' },
  { title: t('navigation.reels'), icon: <ReelIcon />, path: '/reels' },
  { title: t('navigation.publishing'), icon: <PublishIcon />, path: '/publishing' },
  { title: t('navigation.analytics'), icon: <AnalyticsIcon />, path: '/analytics' },
  { title: t('navigation.templates') || 'Templates', icon: <TemplateIcon />, path: '/templates' },
  { title: t('navigation.schedules'), icon: <ScheduleIcon />, path: '/schedules' },
  { title: t('navigation.settings'), icon: <SettingsIcon />, path: '/settings' },
];

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { projects, currentProject, selectProject, fetchProjects } = useProjects();
  const { t } = useTranslation();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDrawerOpen(!drawerOpen);
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
  };

  const handleProjectChange = (projectId: number) => {
    const project = projects && Array.isArray(projects) ? projects.find(p => p.id === projectId) : null;
    selectProject(project || null);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 2, py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography variant="h5" className="gradient-text" sx={{ fontWeight: 700 }}>
            AutoReelr
          </Typography>
          {!isMobile && (
            <IconButton
              onClick={handleDrawerToggle}
              sx={{ ml: 'auto' }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
        </Box>
      </Toolbar>
      
      <Divider sx={{ mx: 2 }} />
      
      {/* Project Selector */}
      <Box sx={{ px: 2, py: 2 }}>
        <FormControl fullWidth size="small">
          <Select
            value={currentProject?.id || ''}
            onChange={(e) => handleProjectChange(e.target.value as number)}
            displayEmpty
            sx={{
              background: 'rgba(255, 255, 255, 0.05)',
              '& .MuiSelect-select': {
                py: 1,
              },
            }}
          >
            <SelectMenuItem value="">
              <em>All Projects</em>
            </SelectMenuItem>
            {projects && Array.isArray(projects) ? projects.map((project) => (
              <SelectMenuItem key={project.id} value={project.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>{project.name}</Typography>
                  <Chip
                    label={`${project.published_reels || 0}/${project.total_reels || 0}`}
                    size="small"
                    sx={{ ml: 'auto' }}
                  />
                </Box>
              </SelectMenuItem>
            )) : null}
          </Select>
        </FormControl>
      </Box>
      
      <List sx={{ px: 1, flexGrow: 1 }}>
        {getMenuItems(t).map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          
          return (
            <motion.div
              key={item.path}
              whileHover={{ x: 4 }}
              whileTap={{ scale: 0.98 }}
            >
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                selected={isActive}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
                    borderLeft: '3px solid #667eea',
                  },
                }}
              >
                <ListItemIcon sx={{ color: isActive ? '#667eea' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.title}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </motion.div>
          );
        })}
      </List>
      
      <Box sx={{ p: 2 }}>
        <Box
          className="glass"
          sx={{
            p: 2,
            borderRadius: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Version 1.0.0
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1 }}>
      <Header onMenuClick={handleDrawerToggle} />
      <Toolbar />
      
      <Box
        component="nav"
        sx={{ 
          width: { md: drawerOpen ? drawerWidth : 0 }, 
          flexShrink: { md: 0 },
          mt: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          open={isMobile ? mobileOpen : drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: '100%',
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Outlet />
        </motion.div>
      </Box>
      
      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled>
          <Box>
            <Typography variant="subtitle2">{user?.username}</Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.is_admin ? t('common.administrator') : t('common.user')}
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/settings'); }}>
          <ListItemIcon>
            <AccountIcon fontSize="small" />
          </ListItemIcon>
          {t('settings.profile')}
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t('common.logout')}
        </MenuItem>
      </Menu>
      
      {/* Notification Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: { width: 320, maxHeight: 400 }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">Notifications</Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleNotificationMenuClose} sx={{ whiteSpace: 'normal', py: 1.5 }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              Reel Published Successfully
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary" 
              sx={{ 
                display: 'block',
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                lineHeight: 1.4
              }}
            >
              Your reel "5 Amazing Facts" was published to Instagram
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleNotificationMenuClose} sx={{ whiteSpace: 'normal', py: 1.5 }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              Processing Complete
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                display: 'block',
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                lineHeight: 1.4
              }}
            >
              3 reels have finished processing and are ready to publish
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={handleNotificationMenuClose} sx={{ whiteSpace: 'normal', py: 1.5 }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              Schedule Reminder
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                display: 'block',
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                lineHeight: 1.4
              }}
            >
              You have 2 reels scheduled for today at 2:00 PM
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
      </Box>
      
      {/* Footer */}
      <Footer />
    </Box>
  );
};

export default Layout;